import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDisplaySettings, type ScreensaverMode } from '../hooks/useDisplaySettings';
import { useTranslation } from '../hooks/useTranslation';
import { dateLocale } from '../i18n';
import { now as clockNow, formatClockTime, formatClockDate } from '../utils/clock';
import { AnalogClock } from './AnalogClock';
import {
  STANDBY_BACKGROUND,
  CLOCK_FONT_STACK,
  CLOCK_GLOW,
  DATE_GLOW,
  CLOCK_FONT_SIZES,
  ANALOG_CLOCK_SIZES,
  DATE_FONT_SIZES,
} from '../constants/standbyStyle';
import { Z_INDEX } from '../constants/zIndex';

export interface ScreensaverPhoto {
  id: number;
  photoUuid: string;
  url: string;
  thumbnailUrl: string | null;
  description: string | null;
  uploaderName: string | null;
  reaction: 'neutral' | 'liked' | 'disliked';
  takenAt: string | null;
  viewCount: number;
  createdAt: string;
}

interface ScreensaverProps {
  photos: ScreensaverPhoto[];
  onWake: () => void;
  onReaction: (photoId: number, reaction: 'liked' | 'disliked') => void;
  nextEvent?: { title: string; startsAt: string; locationName?: string } | null;
  mode?: ScreensaverMode;
}

// Weighted rotation: liked photos appear 3x more, new photos 2x more
function buildWeightedPlaylist(photos: ScreensaverPhoto[]): ScreensaverPhoto[] {
  const now = Date.now();
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const playlist: ScreensaverPhoto[] = [];

  for (const photo of photos) {
    const isNew = now - new Date(photo.createdAt).getTime() < ONE_DAY;
    const weight = photo.reaction === 'liked' ? 3 : isNew ? 2 : 1;
    for (let i = 0; i < weight; i++) {
      playlist.push(photo);
    }
  }

  // Shuffle
  for (let i = playlist.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [playlist[i], playlist[j]] = [playlist[j], playlist[i]];
  }

  return playlist;
}

function getDisplayDuration(photo: ScreensaverPhoto): number {
  if (photo.reaction === 'liked') return 45_000; // 45s for liked
  return 30_000; // 30s default
}

// All three take an explicit locale: passing `[]` would format against the
// WebView's locale instead of the resident's app language — see i18n/locale.ts.
function formatTime(date: Date, locale: string): string {
  return formatClockTime(date, locale);
}

function formatDate(date: Date, locale: string): string {
  return formatClockDate(date, locale, { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatEventTime(isoString: string, locale: string): string {
  return formatClockTime(new Date(isoString), locale);
}

// Standby styling is shared with SleepOverlay — see constants/standbyStyle.ts.

export function Screensaver({ photos, onWake, onReaction, nextEvent, mode: modeProp }: ScreensaverProps) {
  const { settings } = useDisplaySettings();
  const { t, lang } = useTranslation();
  const locale = dateLocale(lang);
  const mode = modeProp || settings.screensaverMode;
  const clockStyle = settings.clockStyle;
  const clockSize = settings.clockSize;

  // Initialize playlist immediately from props to avoid blank first render
  const [playlist, setPlaylist] = useState<ScreensaverPhoto[]>(() =>
    photos.length > 0 ? buildWeightedPlaylist(photos) : []
  );
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showButtons, setShowButtons] = useState(false);
  const [now, setNow] = useState(clockNow);
  const [opacity, setOpacity] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const clockRef = useRef<ReturnType<typeof setInterval>>();
  const buttonTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const failedUrlsRef = useRef(new Set<string>());

  const isPhotoMode = mode === 'photos' && photos.length > 0;

  // Build playlist when photos change
  useEffect(() => {
    if (photos.length > 0) {
      const newPlaylist = buildWeightedPlaylist(photos);
      console.log('[Screensaver] Building playlist from', photos.length, 'photos →', newPlaylist.length, 'entries');
      console.log('[Screensaver] First photo URL:', photos[0]?.url?.substring(0, 100));
      setPlaylist(newPlaylist);
      setCurrentIdx(0);
      setImageLoaded(false);
      setImageError(false);
      failedUrlsRef.current.clear();
    }
  }, [photos]);

  // Clock update every second (for analog) or every 30s (digital)
  useEffect(() => {
    const interval = clockStyle === 'analog' ? 1_000 : 30_000;
    clockRef.current = setInterval(() => setNow(clockNow()), interval);
    return () => { if (clockRef.current) clearInterval(clockRef.current); };
  }, [clockStyle]);

  // Show reaction buttons after 2 seconds (photos mode only)
  useEffect(() => {
    if (!isPhotoMode) return;
    setShowButtons(false);
    buttonTimerRef.current = setTimeout(() => setShowButtons(true), 2000);
    return () => { if (buttonTimerRef.current) clearTimeout(buttonTimerRef.current); };
  }, [currentIdx, isPhotoMode]);

  // Reset image state on index change
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [currentIdx]);

  // Auto-advance to next photo
  useEffect(() => {
    if (!isPhotoMode || playlist.length <= 1) return;
    const currentPhoto = playlist[currentIdx];
    if (!currentPhoto) return;

    const duration = getDisplayDuration(currentPhoto);

    timerRef.current = setTimeout(() => {
      setOpacity(0);
      setTimeout(() => {
        setCurrentIdx((prev) => (prev + 1) % playlist.length);
        setOpacity(1);
      }, 1500);
    }, duration);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [currentIdx, playlist, isPhotoMode]);

  // Handle image load error — skip to next photo or fall back to clock
  const handleImageError = useCallback(() => {
    const photo = playlist[currentIdx];
    if (photo) {
      console.error('[Screensaver] Image failed to load:', photo.url?.substring(0, 120));
      failedUrlsRef.current.add(photo.url);
    }
    setImageError(true);
    // Auto-advance after 2s on error
    setTimeout(() => {
      setCurrentIdx((prev) => (prev + 1) % Math.max(playlist.length, 1));
      setOpacity(1);
    }, 2000);
  }, [currentIdx, playlist]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleReaction = useCallback((reaction: 'liked' | 'disliked') => {
    const photo = playlist[currentIdx];
    if (photo) {
      onReaction(photo.id, reaction);
    }
  }, [currentIdx, playlist, onReaction]);

  const handleTouchToWake = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-reaction-btn]')) return;
    onWake();
  }, [onWake]);

  // ── Clock-only mode ──────────────────────────────────────────
  if (!isPhotoMode) {
    return (
      <div
        onPointerDown={onWake}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: Z_INDEX.SCREENSAVER,
          background: STANDBY_BACKGROUND,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'none',
          userSelect: 'none',
        }}
      >
        {/* Clock */}
        {clockStyle === 'analog' ? (
          <AnalogClock size={ANALOG_CLOCK_SIZES[clockSize]} color="#ffffff" />
        ) : (
          <div style={{
            fontFamily: CLOCK_FONT_STACK,
            fontSize: `${CLOCK_FONT_SIZES[clockSize]}px`,
            fontWeight: 300,
            letterSpacing: '0.02em',
            color: '#ffffff',
            textShadow: CLOCK_GLOW,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
          }}>
            {formatTime(now, locale)}
          </div>
        )}
        {/* Day + Date */}
        <div style={{
          fontFamily: CLOCK_FONT_STACK,
          fontSize: `${DATE_FONT_SIZES[clockSize]}px`,
          fontWeight: 400,
          letterSpacing: '0.04em',
          color: 'rgba(255,255,255,0.85)',
          textShadow: DATE_GLOW,
          marginTop: clockStyle === 'analog' ? '20px' : '16px',
          textAlign: 'center',
        }}>
          {formatDate(now, locale)}
        </div>
        {/* Next event */}
        {nextEvent && (
          <div style={{
            position: 'absolute',
            bottom: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(255,255,255,0.14)',
            backdropFilter: 'blur(8px)',
            padding: '10px 24px',
            borderRadius: '24px',
            color: 'rgba(255,255,255,0.92)',
            fontSize: 'var(--font-body)',
            whiteSpace: 'nowrap',
          }}>
            {nextEvent.title} — {formatEventTime(nextEvent.startsAt, locale)}
          </div>
        )}
      </div>
    );
  }

  // ── Photos mode ──────────────────────────────────────────────
  const currentPhoto = playlist[currentIdx] || playlist[0];
  if (!currentPhoto) {
    console.warn('[Screensaver] No currentPhoto available, playlist length:', playlist.length, 'idx:', currentIdx);
    return null;
  }

  console.log('[Screensaver] Rendering photo', currentIdx, '/', playlist.length, 'url:', currentPhoto.url?.substring(0, 80));

  return (
    <div
      onPointerDown={handleTouchToWake}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.SCREENSAVER,
        backgroundColor: '#000',
        overflow: 'hidden',
        cursor: 'none',
        userSelect: 'none',
      }}
    >
      {/* Photo */}
      <img
        key={currentPhoto.url}
        src={currentPhoto.url}
        alt=""
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: imageError ? 0 : opacity,
          transition: 'opacity 1.5s ease-in-out',
        }}
      />
      {/* Loading/error indicator (top-left, fades after load) */}
      {!imageLoaded && !imageError && (
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          color: 'rgba(255,255,255,0.5)',
          fontSize: 'var(--font-body)',
          zIndex: 2,
        }}>
          Loading photo...
        </div>
      )}
      {imageError && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'rgba(255,255,255,0.4)',
          fontSize: 'var(--font-body)',
          textAlign: 'center',
          zIndex: 2,
        }}>
          <div style={{ fontSize: 'var(--font-heading)', marginBottom: '8px' }}>{'\u{1F4F7}'}</div>
          <div>{t.photos.unavailable}</div>
          <div style={{ fontSize: 'var(--font-small)', marginTop: '4px', opacity: 0.6 }}>Skipping...</div>
        </div>
      )}
      {/* Bottom gradient for text readability */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '40%',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
          pointerEvents: 'none',
        }}
      />
      {/* Clock — bottom left */}
      <div
        style={{
          position: 'absolute',
          bottom: '24px',
          left: '24px',
          color: 'white',
          textShadow: '0 2px 12px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.6)',
        }}
      >
        {clockStyle === 'analog' ? (
          <AnalogClock size={Math.round(ANALOG_CLOCK_SIZES[clockSize] * 0.6)} color="white" />
        ) : (
          <div style={{
            fontSize: `${Math.round(CLOCK_FONT_SIZES[clockSize] * 0.75)}px`,
            fontWeight: 700,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {formatTime(now, locale)}
          </div>
        )}
        <div style={{
          fontSize: `${Math.round(DATE_FONT_SIZES[clockSize] * 0.85)}px`,
          opacity: 0.85,
          marginTop: '6px',
        }}>
          {formatDate(now, locale)}
        </div>
      </div>
      {/* Sender name — bottom center */}
      {currentPhoto.uploaderName && (
        <div
          style={{
            position: 'absolute',
            bottom: '32px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(8px)',
            padding: '8px 20px',
            borderRadius: '24px',
            color: 'white',
            fontSize: 'var(--font-body)',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
          }}
        >
          Sent by {currentPhoto.uploaderName}
        </div>
      )}
      {/* Info — bottom right */}
      <div
        style={{
          position: 'absolute',
          bottom: '24px',
          right: '24px',
          color: 'white',
          textShadow: '0 2px 8px rgba(0,0,0,0.7)',
          textAlign: 'right',
        }}
      >
        {nextEvent && (
          <div style={{ fontSize: 'var(--font-body)', opacity: 0.9, marginBottom: '8px' }}>
            {t.today.nextEvent
              .replace('{title}', nextEvent.title)
              .replace('{time}', formatEventTime(nextEvent.startsAt, locale))}
          </div>
        )}
        {currentPhoto.description && (
          <div style={{ fontSize: 'var(--font-body)', opacity: 0.85, fontStyle: 'italic', maxWidth: '300px' }}>
            {currentPhoto.description}
          </div>
        )}
      </div>
      {/* Reaction buttons — center bottom, appear with fade after 2s */}
      <div
        style={{
          position: 'absolute',
          bottom: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '32px',
          opacity: showButtons ? 1 : 0,
          transition: 'opacity 0.5s ease-in',
          pointerEvents: showButtons ? 'auto' : 'none',
        }}
      >
        <button
          data-reaction-btn="liked"
          onClick={() => handleReaction('liked')}
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            border: 'none',
            // T7.4 / E4 — token-driven; --caro-success swaps per theme (Light/Dark/HC).
            // Selected state uses the solid token; unselected dims with alpha overlay.
            backgroundColor: 'var(--caro-success)',
            opacity: currentPhoto.reaction === 'liked' ? 1 : 0.7,
            color: 'white',
            fontSize: 'var(--font-heading)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            transition: 'transform 0.2s, opacity 0.2s',
          }}
          aria-label="Like this photo"
        >
          &#x2764;
        </button>
        <button
          data-reaction-btn="disliked"
          onClick={() => handleReaction('disliked')}
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            border: 'none',
            // T7.4 / E4 — token-driven neutral; --caro-text-light is theme-aware
            // (gray in Light, lighter gray in Dark, white in HC). 0.7 alpha keeps
            // the photo behind the button readable.
            backgroundColor: 'var(--caro-text-light)',
            opacity: 0.7,
            color: 'white',
            fontSize: 'var(--font-heading)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            transition: 'transform 0.2s',
          }}
          aria-label="Hide this photo"
        >
          &#x2715;
        </button>
      </div>
    </div>
  );
}
